import React, { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { motion } from 'framer-motion';
import { useForm } from 'react-hook-form';
import { ArrowRight, CheckCircle, Mail, Lock, User, Building, } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { authService } from '../services/authService';
import SearchableSelect from '../components/SearchableSelect';
import COUNTRIES, { getTimezonesForCountry, getTimezoneLabel } from '../constants/countries';

const SignUpPage = () => {
  const { t } = useTranslation();
  const [step, setStep] = useState(1); // 1: Account, 2: Company, 3: Success
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [isTrial, setIsTrial] = useState(true); // Default to trial
  const [selectedServices, setSelectedServices] = useState(['CMMS', 'CHATBOT']); // Default both
  const [selectedCountry, setSelectedCountry] = useState('');
  const [selectedIndustry, setSelectedIndustry] = useState('');
  const { register, handleSubmit, formState: { errors }, watch, setValue, trigger } = useForm();
  const password = watch('password');

  const countryOptions = useMemo(() =>
    COUNTRIES.map(c => ({ value: c.code, label: c.name })),
    []
  );

  const timezoneOptions = useMemo(() => {
    if (!selectedCountry) return [];
    return getTimezonesForCountry(selectedCountry).map(tz => ({
      value: tz,
      label: getTimezoneLabel(tz),
    }));
  }, [selectedCountry]);

  const industryOptions = [
    'Manufacturing', 'Healthcare', 'Technology', 'Retail',
    'Construction', 'Transportation', 'Energy', 'Education',
    'Hospitality', 'Other',
  ].map(i => ({ value: i, label: i }));

  // When country changes, auto-fill timezone with the first option
  useEffect(() => {
    if (selectedCountry) {
      const tzs = getTimezonesForCountry(selectedCountry);
      if (tzs.length > 0) {
        setValue('timezone', tzs[0]);
      }
      const country = COUNTRIES.find(c => c.code === selectedCountry);
      if (country) {
        setValue('country', country.name);
      }
    } else {
      setValue('timezone', '');
      setValue('country', '');
    }
  }, [selectedCountry, setValue]);

  // Every input on step 1 is required. The Continue button used to call setStep(2)
  // directly, which skipped validation entirely: you could advance with the whole
  // form empty and only find out at submit, where the messages render against
  // inputs step 2 no longer shows.
  const STEP_1_FIELDS = ['firstName', 'lastName', 'email', 'password', 'confirmPassword'];

  const handleContinue = async () => {
    const valid = await trigger(STEP_1_FIELDS);
    if (valid) {
      setError('');
      setStep(2);
    }
  };

  // Belt and braces: if a submit ever fails on a step-1 field, go back to the step
  // that shows it rather than failing silently on a hidden input.
  const onInvalid = (formErrors) => {
    if (STEP_1_FIELDS.some(field => formErrors[field])) {
      setStep(1);
    }
  };

  const toggleService = (service) => {
    if (selectedServices.includes(service)) {
      setSelectedServices(selectedServices.filter(s => s !== service));
    } else {
      setSelectedServices([...selectedServices, service]);
    }
  };

  const onSubmit = async (data) => {
    setLoading(true);
    setError('');

    if (isTrial && selectedServices.length === 0) {
      setError(t('signup.selectService'));
      setLoading(false);
      return;
    }

    try {
      // Register user with backend
      await authService.register({
        email: data.email,
        password: data.password,
        firstName: data.firstName,
        lastName: data.lastName,
        companyName: data.companyName,
        industry: data.industry,
        companySize: data.companySize,
        phone: data.phone,
        country: data.country,
        timezone: data.timezone,
        isTrial: isTrial,
        interestedServices: isTrial ? selectedServices : [],
      });

      // Show success screen first
      setStep(3);

      // Wait 2 seconds then redirect to login
      setTimeout(() => {
        window.location.href = '/login';
      }, 2000);

    } catch (err) {
      setError(err.message || 'Registration failed. Please try again.');
      setLoading(false);
      setStep(1); // Go back to first step to show error
    }
  };

  const benefits = [
    t('signup.benefit1'),
    t('signup.benefit2'),
    t('signup.benefit3'),
    t('signup.benefit4'),
  ];

  return (
    <>
      <Helmet>
        <title>Sign Up - Start Free Trial | mwbTechs</title>
        <meta
          name="description"
          content="Create your free account. 14-day trial, no credit card required. Get started in minutes."
        />
      </Helmet>

      <div className="pt-20 min-h-screen bg-gray-50">
        <div className="container-custom py-12">
          <div className="max-w-6xl mx-auto">
            <div className="grid lg:grid-cols-2 gap-12 items-start">
              {/* Left Side - Benefits */}
              <motion.div
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.6 }}
                className="lg:sticky lg:top-24"
              >
                <h1 className="text-4xl font-bold text-gray-900 mb-4">
                  {t('signup.title')}
                </h1>
                <p className="text-xl text-gray-600 mb-8">
                  {t('signup.subtitle')}
                </p>

                <div className="space-y-4 mb-8">
                  {benefits.map((benefit, index) => (
                    <div key={index} className="flex items-start gap-3">
                      <CheckCircle className="w-6 h-6 text-green-500 flex-shrink-0 mt-0.5" />
                      <span className="text-gray-700">{benefit}</span>
                    </div>
                  ))}
                </div>

                <div className="bg-white rounded-xl p-6 shadow-lg border border-gray-200">
                  <h3 className="font-bold text-gray-900 mb-4">{t('signup.includedTitle')}</h3>
                  <ul className="space-y-2 text-sm text-gray-600">
                    <li>✓ {t('signup.included1')}</li>
                    <li>✓ {t('signup.included2')}</li>
                    <li>✓ {t('signup.included3')}</li>
                    <li>✓ {t('signup.included4')}</li>
                    <li>✓ {t('signup.included5')}</li>
                    <li>✓ {t('signup.included6')}</li>
                  </ul>
                </div>
              </motion.div>

              {/* Right Side - Form */}
              <motion.div
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.6, delay: 0.2 }}
              >
                {step < 3 ? (
                  <div className="bg-white rounded-2xl shadow-xl p-8 border border-gray-200">
                    {/* Progress Steps */}
                    <div className="flex items-center justify-center mb-8">
                      <div className="flex items-center gap-4">
                        <div className={`w-8 h-8 rounded-full flex items-center justify-center font-bold ${
                          step >= 1 ? 'bg-primary-600 text-white' : 'bg-gray-200 text-gray-600'
                        }`}>
                          1
                        </div>
                        <div className={`w-16 h-0.5 ${step >= 2 ? 'bg-primary-600' : 'bg-gray-200'}`}></div>
                        <div className={`w-8 h-8 rounded-full flex items-center justify-center font-bold ${
                          step >= 2 ? 'bg-primary-600 text-white' : 'bg-gray-200 text-gray-600'
                        }`}>
                          2
                        </div>
                      </div>
                    </div>

                    {/* Error Display */}
                    {error && (
                      <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
                        <p className="text-sm text-red-600">{error}</p>
                      </div>
                    )}

                    <form onSubmit={handleSubmit(onSubmit, onInvalid)} className="space-y-6">
                      {step === 1 && (
                        <>
                          <div>
                            <h2 className="text-2xl font-bold text-gray-900 mb-2">
                              {t('signup.stepAccount')}
                            </h2>
                            <p className="text-gray-600 mb-6">
                              {t('signup.stepAccountDesc')}
                            </p>
                          </div>

                          <div className="grid grid-cols-2 gap-4">
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-2">
                                {t('signup.firstName')} *
                              </label>
                              <div className="relative">
                                <User className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                                <input
                                  {...register('firstName', { required: t('signup.firstNameRequired') })}
                                  className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                                  placeholder="John"
                                />
                              </div>
                              {errors.firstName && (
                                <p className="mt-1 text-sm text-red-600">{errors.firstName.message}</p>
                              )}
                            </div>

                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-2">
                                {t('signup.lastName')} *
                              </label>
                              <input
                                {...register('lastName', { required: t('signup.lastNameRequired') })}
                                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                                placeholder="Doe"
                              />
                              {errors.lastName && (
                                <p className="mt-1 text-sm text-red-600">{errors.lastName.message}</p>
                              )}
                            </div>
                          </div>

                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                              {t('signup.emailAddress')} *
                            </label>
                            <div className="relative">
                              <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                              <input
                                type="email"
                                {...register('email', {
                                  required: t('signup.emailRequired'),
                                  pattern: {
                                    value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                                    message: t('signup.invalidEmail')
                                  }
                                })}
                                className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                                placeholder="john@company.com"
                              />
                            </div>
                            {errors.email && (
                              <p className="mt-1 text-sm text-red-600">{errors.email.message}</p>
                            )}
                          </div>

                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                              {t('signup.password')} *
                            </label>
                            <div className="relative">
                              <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                              <input
                                type="password"
                                {...register('password', {
                                  required: t('signup.passwordRequired'),
                                  minLength: {
                                    value: 8,
                                    message: t('signup.passwordMinLength')
                                  }
                                })}
                                className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                                placeholder="••••••••"
                              />
                            </div>
                            {errors.password && (
                              <p className="mt-1 text-sm text-red-600">{errors.password.message}</p>
                            )}
                          </div>

                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                              {t('signup.confirmPassword')} *
                            </label>
                            <div className="relative">
                              <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                              <input
                                type="password"
                                {...register('confirmPassword', {
                                  required: t('signup.confirmPasswordRequired'),
                                  validate: value => value === password || t('signup.passwordsMustMatch')
                                })}
                                className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                                placeholder="••••••••"
                              />
                            </div>
                            {errors.confirmPassword && (
                              <p className="mt-1 text-sm text-red-600">{errors.confirmPassword.message}</p>
                            )}
                          </div>

                          {/* Trial Selection */}
                          <div className="bg-gradient-to-r from-primary-50 to-blue-50 border-2 border-primary-200 rounded-lg p-4">
                            <label className="flex items-start gap-3 cursor-pointer">
                              <input
                                type="checkbox"
                                checked={isTrial}
                                onChange={(e) => setIsTrial(e.target.checked)}
                                className="mt-1 w-5 h-5 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
                              />
                              <div>
                                <span className="text-gray-900 font-semibold block">
                                  {t('signup.trialLabel')}
                                </span>
                                <span className="text-sm text-gray-600">
                                  {t('signup.trialDesc')}
                                </span>
                              </div>
                            </label>
                          </div>


                          <button
                            type="button"
                            onClick={handleContinue}
                            className="w-full btn btn-primary text-lg"
                          >
                            {t('signup.continue')}
                            <ArrowRight className="w-5 h-5 ml-2" />
                          </button>
                        </>
                      )}

                      {step === 2 && (
                        <>
                          <div>
                            <h2 className="text-2xl font-bold text-gray-900 mb-2">
                              {t('signup.stepCompany')}
                            </h2>
                            <p className="text-gray-600 mb-6">
                              {t('signup.stepCompanyDesc')}
                            </p>
                          </div>

                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                              {t('signup.companyName')} *
                            </label>
                            <div className="relative">
                              <Building className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                              <input
                                {...register('companyName', { required: t('signup.companyNameRequired') })}
                                className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                                placeholder="Acme Corp"
                              />
                            </div>
                            {errors.companyName && (
                              <p className="mt-1 text-sm text-red-600">{errors.companyName.message}</p>
                            )}
                          </div>

                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                              {t('signup.industry')}
                            </label>
                            <input type="hidden" {...register('industry')} />
                            <SearchableSelect
                              options={industryOptions}
                              value={selectedIndustry}
                              onChange={(val) => {
                                setSelectedIndustry(val);
                                setValue('industry', val);
                              }}
                              placeholder="Select industry"
                            />
                          </div>

                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                              {t('signup.companySize')} <span className="text-red-500">*</span>
                            </label>
                            <select
                              {...register('companySize', { required: t('signup.companySizeRequired') })}
                              className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent ${errors.companySize ? 'border-red-500' : 'border-gray-300'}`}
                            >
                              <option value="">{t('signup.selectSize')}</option>
                              <option value="SMALL">{t('signup.small')}</option>
                              <option value="MEDIUM">{t('signup.medium')}</option>
                              <option value="LARGE">{t('signup.large')}</option>
                              <option value="ENTERPRISE">{t('signup.enterprise')}</option>
                            </select>
                            {errors.companySize && (
                              <p className="mt-1 text-sm text-red-500">{errors.companySize.message}</p>
                            )}
                          </div>

                          <div className="grid grid-cols-2 gap-4">
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-2">
                                {t('signup.phone')}
                              </label>
                              <input
                                type="tel"
                                {...register('phone')}
                                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                                placeholder="+1 (555) 123-4567"
                              />
                            </div>

                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-2">
                                {t('signup.country')}
                              </label>
                              <input type="hidden" {...register('country')} />
                              <SearchableSelect
                                options={countryOptions}
                                value={selectedCountry}
                                onChange={setSelectedCountry}
                                placeholder="Select country"
                              />
                            </div>
                          </div>

                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                              {t('signup.timezone')}
                            </label>
                            {timezoneOptions.length > 0 ? (
                              <select
                                {...register('timezone')}
                                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                              >
                                {timezoneOptions.map(tz => (
                                  <option key={tz.value} value={tz.value}>
                                    {tz.label}
                                  </option>
                                ))}
                              </select>
                            ) : (
                              <select
                                disabled
                                className="w-full px-4 py-3 border border-gray-300 rounded-lg bg-gray-50 text-gray-400"
                              >
                                <option value="">{t('signup.selectCountryFirst')}</option>
                              </select>
                            )}
                          </div>

                          {isTrial && (
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-2">
                                {t('signup.servicesInterested')} <span className="text-red-500">*</span>
                              </label>
                              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                {/* CMMS card */}
                                <div
                                  onClick={() => toggleService('CMMS')}
                                  className={`border-2 rounded-lg p-4 cursor-pointer transition-all ${
                                    selectedServices.includes('CMMS')
                                      ? 'border-blue-500 bg-blue-50'
                                      : 'border-gray-200 hover:border-gray-300'
                                  }`}
                                >
                                  <div className="flex items-center gap-2">
                                    <div className={`w-4 h-4 rounded border-2 flex items-center justify-center ${
                                      selectedServices.includes('CMMS') ? 'border-blue-500 bg-blue-500' : 'border-gray-300'
                                    }`}>
                                      {selectedServices.includes('CMMS') && (
                                        <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
                                          <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                        </svg>
                                      )}
                                    </div>
                                    <span className="font-medium text-gray-900">{t('signup.cmmsName')}</span>
                                  </div>
                                  <p className="text-xs text-gray-500 mt-1 ml-6">{t('signup.cmmsDesc')}</p>
                                </div>

                                {/* AI Chatbot card */}
                                <div
                                  onClick={() => toggleService('CHATBOT')}
                                  className={`border-2 rounded-lg p-4 cursor-pointer transition-all ${
                                    selectedServices.includes('CHATBOT')
                                      ? 'border-blue-500 bg-blue-50'
                                      : 'border-gray-200 hover:border-gray-300'
                                  }`}
                                >
                                  <div className="flex items-center gap-2">
                                    <div className={`w-4 h-4 rounded border-2 flex items-center justify-center ${
                                      selectedServices.includes('CHATBOT') ? 'border-blue-500 bg-blue-500' : 'border-gray-300'
                                    }`}>
                                      {selectedServices.includes('CHATBOT') && (
                                        <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
                                          <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                        </svg>
                                      )}
                                    </div>
                                    <span className="font-medium text-gray-900">{t('signup.chatbotName')}</span>
                                  </div>
                                  <p className="text-xs text-gray-500 mt-1 ml-6">{t('signup.chatbotDesc')}</p>
                                </div>
                              </div>
                            </div>
                          )}

                          <div className="flex items-start gap-3">
                            <input
                              type="checkbox"
                              {...register('terms', { required: t('signup.acceptTerms') })}
                              className="mt-1"
                            />
                            <label className="text-sm text-gray-600">
                              {t('signup.agreeTerms')}{' '}
                              <Link to="/terms" className="text-primary-600 hover:underline">
                                {t('signup.termsOfService')}
                              </Link>{' '}
                              {t('signup.and')}{' '}
                              <Link to="/privacy" className="text-primary-600 hover:underline">
                                {t('signup.privacyPolicy')}
                              </Link>
                            </label>
                          </div>
                          {errors.terms && (
                            <p className="text-sm text-red-600">{errors.terms.message}</p>
                          )}

                          <div className="flex gap-4">
                            <button
                              type="button"
                              onClick={() => setStep(1)}
                              className="flex-1 btn btn-outline"
                            >
                              {t('signup.back')}
                            </button>
                            <button
                              type="submit"
                              disabled={loading}
                              className="flex-1 btn btn-primary text-lg disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                              {loading ? (
                                <>
                                  <span className="inline-block w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></span>
                                  {t('signup.creatingAccount')}
                                </>
                              ) : (
                                <>
                                  {t('signup.startFreeTrial')}
                                  <ArrowRight className="w-5 h-5 ml-2" />
                                </>
                              )}
                            </button>
                          </div>
                        </>
                      )}
                    </form>

                    <div className="mt-6 text-center text-sm text-gray-600">
                      {t('signup.alreadyHaveAccount')}{' '}
                      <Link to="/login" className="text-primary-600 hover:underline font-medium">
                        {t('signup.signIn')}
                      </Link>
                    </div>
                  </div>
                ) : (
                  <div className="bg-white rounded-2xl shadow-xl p-8 border border-gray-200 text-center">
                    <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
                      <CheckCircle className="w-10 h-10 text-green-600" />
                    </div>
                    <h2 className="text-3xl font-bold text-gray-900 mb-4">
                      {t('signup.welcomeTitle')}
                    </h2>
                    <p className="text-gray-600 mb-8">
                      {t('signup.welcomeDesc')}
                    </p>
                    <Link to="/login" className="btn btn-primary text-lg inline-flex items-center">
                      {t('signup.goToLogin')}
                      <ArrowRight className="w-5 h-5 ml-2" />
                    </Link>
                  </div>
                )}
              </motion.div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default SignUpPage;
